// App-wide toast notifications — replaces the inline flash banners that
// pushed page content down every time an action reported back.
//
// useFlash() returns a setter shaped like the old `setFlash` state setter
// ({ kind, msg } or null), so pages swap one line instead of rewriting every
// call site. null is a no-op: toasts dismiss themselves, nothing to clear.
// Errors stay until closed — a load failure behind a spinner must not vanish
// before the operator has read it.

import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { CheckCircle2, Info, AlertTriangle, XCircle, X } from 'lucide-react';

const ToastContext = createContext(null);

const AUTO_DISMISS_MS = { success: 4000, info: 4000, warning: 6000 };
const ICONS = { success: CheckCircle2, info: Info, warning: AlertTriangle, danger: XCircle };
const MAX_TOASTS = 4;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((list) => list.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    window.setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), 200);
  }, []);

  const flash = useCallback((f) => {
    if (!f || !f.msg) return;
    const kind = f.kind === 'error' ? 'danger' : (ICONS[f.kind] ? f.kind : 'info');
    const id = ++nextId.current;
    // A repeat of the message already on screen replaces it rather than stacking.
    setToasts((list) => [...list.filter((t) => t.msg !== f.msg), { id, kind, msg: f.msg }].slice(-MAX_TOASTS));
    const ms = AUTO_DISMISS_MS[kind];
    if (ms) window.setTimeout(() => dismiss(id), ms);
  }, [dismiss]);

  return (
    <ToastContext.Provider value={flash}>
      {children}
      <div className="app-toasts" aria-live="polite">
        {toasts.map((t) => {
          const Icon = ICONS[t.kind];
          return (
            <div
              key={t.id}
              className={`app-toast app-toast--${t.kind}${t.leaving ? ' is-leaving' : ''}`}
              role={t.kind === 'danger' ? 'alert' : 'status'}
            >
              <Icon size={18} className="app-toast__icon" aria-hidden="true" />
              <div className="app-toast__msg">{t.msg}</div>
              <button type="button" className="app-toast__close" aria-label="Close" onClick={() => dismiss(t.id)}>
                <X size={16} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useFlash() {
  return useContext(ToastContext);
}
